import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChartBar, Package, Receipt, EnvelopeSimple, Wrench, Plus, PencilSimple, Trash, X, SignOut, CurrencyGbp, Users, Bell, Gear, Article, PhoneCall,
} from "@phosphor-icons/react";
import api, { gbp, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import ImageUpload from "@/components/ImageUpload";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminBlog from "@/components/admin/AdminBlog";
import { toast } from "sonner";

const EMPTY = { slug: "", name: "", category: "road", brand: "", price: 0, sale_price: "", short_description: "", description: "", images: "", is_featured: false, stock: 10, rating: 4.5 };
const CATS = ["road", "mountain", "electric", "gravel", "framesets", "surron", "components"];

export default function Admin() {
  const { user, loading, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [calls, setCalls] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [replyDraft, setReplyDraft] = useState({});

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) navigate("/login", { state: { from: "/admin" } });
  }, [user, isAdmin, loading, navigate]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/call-requests").then((r) => setCalls(r.data)).catch(() => {});
  };
  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin]);

  if (loading || !isAdmin) return <div className="container-px py-32 text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" /></div>;

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      price: Number(editing.price),
      sale_price: editing.sale_price ? Number(editing.sale_price) : null,
      stock: Number(editing.stock),
      rating: Number(editing.rating),
      images: typeof editing.images === "string" ? editing.images.split(",").map((s) => s.trim()).filter(Boolean) : editing.images,
    };
    try {
      if (editing._isNew) await api.post("/products", payload);
      else await api.put(`/products/${editing._origSlug}`, payload);
      toast.success("Product saved");
      setEditing(null); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const delProduct = async (slug) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${slug}`); toast.success("Deleted"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendReply = async (id) => {
    const reply = (replyDraft[id] || "").trim();
    if (!reply) { toast.error("Write a reply first"); return; }
    try {
      const { data } = await api.post(`/admin/messages/${id}/reply`, { reply });
      toast.success(data.emailed ? "Reply saved & emailed to customer" : "Reply saved — visible in the customer's account (email sends once Resend key is added)");
      setReplyDraft((d) => ({ ...d, [id]: "" }));
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const markPaid = async (orderId) => {
    if (!window.confirm("Mark this order as paid? A confirmation email will be sent to the customer.")) return;
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/mark-paid`);
      toast.success(data.emailed ? "Order marked as paid — confirmation emailed to customer" : "Order marked as paid (email sends once Resend key is added)");
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const markCallDone = async (id) => {
    try {
      await api.post(`/admin/call-requests/${id}/status`, { status: "contacted" });
      toast.success("Marked as contacted");
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const TABS = [
    { v: "overview", l: "Overview", I: ChartBar },
    { v: "products", l: "Products", I: Package },
    { v: "blog", l: "Blog", I: Article },
    { v: "orders", l: "Orders", I: Receipt },
    { v: "calls", l: "Call Requests", I: PhoneCall },
    { v: "messages", l: "Messages", I: EnvelopeSimple },
    { v: "bookings", l: "Bookings", I: Wrench },
    { v: "settings", l: "Settings", I: Gear },
  ];

  return (
    <>
      <SEO title="Admin Dashboard" description="Store administration" path="/admin" />
      <div className="container-px py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="overline">Admin</p>
            <h1 className="text-3xl font-black tracking-tight">Dashboard</h1>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="btn-outline !py-2.5 text-sm" data-testid="logout-btn"><SignOut size={18} /> Sign out</button>
        </div>

        <div className="mt-8 flex gap-2 overflow-x-auto border-b border-border no-scrollbar">
          {TABS.map(({ v, l, I }) => (
            <button key={v} onClick={() => setTab(v)} className={`shrink-0 inline-flex items-center gap-2 border-b-2 px-5 py-3 text-sm font-bold transition-colors ${tab === v ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`admin-tab-${v}`}>
              <I size={18} /> {l}
            </button>
          ))}
        </div>

        <div className="py-8">
          {tab === "overview" && stats && (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
              {[[CurrencyGbp, gbp(stats.revenue), "Revenue"], [Receipt, stats.orders, "Total orders"], [Package, stats.products, "Products"], [Users, stats.users, "Customers"], [EnvelopeSimple, stats.newsletter, "Subscribers"], [Bell, stats.messages, "Messages"], [Wrench, stats.bookings, "Bookings"], [Receipt, stats.paid_orders, "Paid orders"]].map(([I, n, l], i) => (
                <div key={i} className="rounded-3xl border border-border bg-card p-6">
                  <I size={26} className="text-primary" weight="bold" />
                  <div className="mt-3 text-3xl font-black">{n}</div>
                  <div className="text-sm text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "products" && (
            <div>
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{products.length} products</h2>
                <button onClick={() => setEditing({ ...EMPTY, _isNew: true })} className="btn-primary !py-2.5 text-sm" data-testid="admin-add-product"><Plus size={18} /> Add product</button>
              </div>
              <div className="mt-5 overflow-x-auto rounded-2xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-left"><tr>
                    <th className="p-4 font-semibold">Product</th><th className="p-4 font-semibold">Category</th><th className="p-4 font-semibold">Price</th><th className="p-4 font-semibold">Stock</th><th className="p-4 font-semibold text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} className="border-t border-border" data-testid={`admin-product-${p.slug}`}>
                        <td className="p-4"><div className="flex items-center gap-3"><img src={p.thumbnail} alt={p.name} className="h-10 w-10 rounded-lg object-cover" loading="lazy" /><span className="font-semibold">{p.name}</span></div></td>
                        <td className="p-4 capitalize">{p.category.replace("-", " ")}</td>
                        <td className="p-4 font-semibold">{gbp(p.sale_price || p.price)}</td>
                        <td className="p-4">{p.stock}</td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditing({ ...p, _origSlug: p.slug, images: (p.images || []).join(", "), sale_price: p.sale_price || "" })} className="grid h-9 w-9 place-items-center rounded-lg border border-border hover:border-primary hover:text-primary" aria-label="Edit"><PencilSimple size={16} /></button>
                            <button onClick={() => delProduct(p.slug)} className="grid h-9 w-9 place-items-center rounded-lg border border-border hover:border-primary hover:text-primary" aria-label="Delete"><Trash size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "orders" && (
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left"><tr><th className="p-4 font-semibold">Order</th><th className="p-4 font-semibold">Customer</th><th className="p-4 font-semibold">Total</th><th className="p-4 font-semibold">Status</th><th className="p-4 font-semibold text-right">Actions</th></tr></thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No orders yet.</td></tr>}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-border" data-testid={`admin-order-${o.order_number || o.order_id?.slice(0, 8)}`}>
                      <td className="p-4 font-mono">#{o.order_number || o.order_id?.slice(0, 8)}</td>
                      <td className="p-4"><div className="font-semibold">{o.shipping?.name || "—"}</div><div className="text-xs text-muted-foreground">{o.email}</div></td>
                      <td className="p-4 font-semibold">
                        {gbp(o.amount)}
                        {o.currency && o.currency.toUpperCase() !== "GBP" && (
                          <span className="ml-1 text-xs text-muted-foreground">({money(o.amount * (o.fx_rate || 1), o.currency)})</span>
                        )}
                      </td>
                      <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap ${o.payment_status === "paid" ? "bg-green-500/10 text-green-600" : "bg-amber-500/10 text-amber-600"}`}>{o.payment_status === "paid" ? "Paid" : "Pending Payment — Awaiting Bank Transfer"}</span></td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setViewOrder(o)} className="btn-outline !px-4 !py-1.5 text-xs whitespace-nowrap" data-testid={`view-order-${o.order_number || o.order_id?.slice(0, 8)}`}>Details</button>
                          {o.payment_status !== "paid" && (
                            <button onClick={() => markPaid(o.order_id)} className="btn-primary !px-4 !py-1.5 text-xs whitespace-nowrap" data-testid={`mark-paid-${o.order_number || o.order_id?.slice(0, 8)}`}>Mark as Paid</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "calls" && (
            <div className="space-y-4" data-testid="admin-call-requests">
              {calls.length === 0 && <p className="text-muted-foreground">No call requests yet.</p>}
              {calls.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-call-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{c.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${c.status === "contacted" ? "bg-green-500/10 text-green-600" : "bg-amber-500/10 text-amber-600"}`}>{c.status}</span>
                      <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString("en-GB")}</span>
                    </div>
                  </div>
                  <p className="text-sm text-primary">{c.phone} · {c.email}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Preferred: {c.preferred_time || "Anytime"}{c.message && ` — ${c.message}`}</p>
                  {c.status !== "contacted" && (
                    <button onClick={() => markCallDone(c.id)} className="btn-outline mt-3 !py-2 text-xs" data-testid={`call-done-${c.id}`}>Mark as contacted</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "messages" && (
            <div className="space-y-4">
              {messages.length === 0 && <p className="text-muted-foreground">No messages.</p>}
              {messages.map((m) => (
                <div key={m.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{m.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${m.status === "replied" ? "bg-green-500/10 text-green-600" : "bg-amber-500/10 text-amber-600"}`}>{m.status || "new"}</span>
                      <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString("en-GB")}</span>
                    </div>
                  </div>
                  <p className="text-sm text-primary">{m.email} {m.subject && `· ${m.subject}`}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{m.message}</p>
                  {m.reply ? (
                    <div className="mt-3 rounded-xl bg-muted p-4 text-sm" data-testid={`admin-reply-${m.id}`}>
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Replied {m.replied_by && `by ${m.replied_by}`} {m.replied_at && `· ${new Date(m.replied_at).toLocaleDateString("en-GB")}`}</p>
                      <p className="mt-1">{m.reply}</p>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-end gap-2">
                      <textarea rows={2} placeholder="Write a reply to this customer…" value={replyDraft[m.id] || ""} onChange={(e) => setReplyDraft({ ...replyDraft, [m.id]: e.target.value })} className="flex-1 rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid={`reply-input-${m.id}`} />
                      <button onClick={() => sendReply(m.id)} className="btn-primary !py-2.5 text-sm shrink-0" data-testid={`reply-send-${m.id}`}>Reply</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "bookings" && (
            <div className="space-y-4">
              {bookings.length === 0 && <p className="text-muted-foreground">No bookings.</p>}
              {bookings.map((b) => (
                <div key={b.id} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold capitalize">{b.type} · {b.name}</span>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-bold capitalize">{b.status}</span>
                  </div>
                  <p className="text-sm text-primary">{b.email} · {b.phone}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{b.bike_model && `Bike: ${b.bike_model}. `}{b.details} {b.date && `(Preferred: ${b.date})`}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "blog" && <AdminBlog />}

          {tab === "settings" && <AdminSettings />}
        </div>
      </div>

      {/* Order details modal */}
      {viewOrder && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid="order-details-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setViewOrder(null)} />
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-soft-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black">Order #{viewOrder.order_number || viewOrder.order_id?.slice(0, 8)}</h3>
              <button onClick={() => setViewOrder(null)} aria-label="Close" data-testid="close-order-details"><X size={22} /></button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{new Date(viewOrder.created_at).toLocaleString("en-GB")} · {viewOrder.payment_status === "paid" ? "Paid" : "Pending Payment — Awaiting Bank Transfer"}</p>
            <div className="mt-4 rounded-2xl border border-border p-4 text-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Customer</p>
              {[["Name", viewOrder.shipping?.name], ["Email", viewOrder.email], ["Street address", viewOrder.shipping?.address], ["ZIP / Postcode", viewOrder.shipping?.postcode], ["City", viewOrder.shipping?.city], ["Country", viewOrder.shipping?.country], ["Phone", viewOrder.shipping?.phone]].map(([l, v]) => v ? (
                <div key={l} className="mt-1.5 flex justify-between gap-4"><span className="text-muted-foreground">{l}</span><span className="font-semibold text-right">{v}</span></div>
              ) : null)}
            </div>
            <div className="mt-4 rounded-2xl border border-border p-4 text-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Items</p>
              {viewOrder.items?.map((it) => (
                <div key={it.slug} className="mt-2 flex items-center gap-3">
                  <img src={it.thumbnail} alt={it.name} className="h-9 w-9 rounded-lg object-cover" loading="lazy" />
                  <span className="flex-1">{it.name} × {it.quantity}</span>
                  <span className="font-semibold">{gbp(it.unit_price * it.quantity)}</span>
                </div>
              ))}
              <div className="mt-3 flex justify-between border-t border-border pt-2 font-black">
                <span>Total</span>
                <span>{gbp(viewOrder.amount)}{viewOrder.currency && viewOrder.currency.toUpperCase() !== "GBP" && <span className="ml-1 text-xs font-semibold text-muted-foreground">({money(viewOrder.amount * (viewOrder.fx_rate || 1), viewOrder.currency)})</span>}</span>
              </div>
            </div>
            {viewOrder.payment_status !== "paid" && (
              <button onClick={() => { markPaid(viewOrder.order_id); setViewOrder(null); }} className="btn-primary mt-4 w-full" data-testid="modal-mark-paid">Mark as Paid</button>
            )}
          </div>
        </div>
      )}

      {/* Product editor */}
      {editing && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid="product-editor">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={saveProduct} className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-soft-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black">{editing._isNew ? "Add product" : "Edit product"}</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close"><X size={22} /></button>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <input required placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input required placeholder="Slug (url-friendly)" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input placeholder="Brand" value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input required type="number" step="0.01" placeholder="Price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input type="number" step="0.01" placeholder="Sale price (optional)" value={editing.sale_price} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input type="number" placeholder="Stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input type="number" step="0.1" placeholder="Rating" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
            <input placeholder="Short description" value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} className="mt-3 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
            <input placeholder='Badge (e.g. "Fairly Discounted", "The Icon")' value={editing.badge || ""} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} className="mt-3 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
            <textarea rows={3} placeholder="Full description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-3 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
            <div className="mt-3">
              <span className="text-xs font-semibold text-muted-foreground">Product images</span>
              <div className="mt-1"><ImageUpload value="" label="image" testid="product-image-upload" onUploaded={(url) => setEditing({ ...editing, images: editing.images ? `${editing.images}, ${url}` : url })} /></div>
            </div>
            <textarea rows={2} placeholder="Image URLs (comma separated)" value={editing.images} onChange={(e) => setEditing({ ...editing, images: e.target.value })} className="mt-3 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
            <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} className="accent-primary" /> Featured product</label>
            <button className="btn-primary mt-5 w-full">Save product</button>
          </form>
        </div>
      )}
    </>
  );
}
